import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Switch,
} from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import {
    useCreateDraftClaim,
    useSplitClaimsToNewClaim,
    useUpdateClaimsActivity,
} from "../../hooks/claims.mutations";

import ListState from "../../../../components/ListState/ListState";
import SingleClaim from "../Claim/SingleClaim";
import EditClaims from "../EditClaims/EditClaims";
import { useGetClaim, useGetParentClaim } from "../../hooks/claims.queries";
import ClaimSubmissionHistory from "../ClaimSubmissionHistory/ClaimSubmissionHistory";
import { formatDate } from "../../../../utils/helpers";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import { useFetchInvoices } from "../../../billing/hooks/queries/billing.queries";
import ManualRemitForm from "../../../remits/components/ManualRemitForm/ManualRemitForm";
import { useFetchRemitClaims } from "../../../remits/hooks/remits.queries";
import { ClaimActivityType, ClaimStatus } from "../../types/claims.types";
import { getClaimActivityToDisplay } from "../../helpers/claims.helper";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import Loader from "../../../../shared-ui/Loader/Loader";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";
import LogOutIcon from "../../../../icons/LogOut";
import CreateClaimWithInsurance from "../CreateClaimWithInsurance/CreateClaimWithInsurance";

type SubmitTypes = "save" | "save-continue";

interface ClaimsWrapperProps {
    allowUserGenerate?: boolean;
}

interface SwitchProps {
    title: React.ReactNode;
    disabled?: boolean;
    onClick: (title: string) => void;
    key: string;
}

type Dialogs =
    | "add_remit"
    | "update_claims_activity"
    | "create-claim-with-insurance"
    | "detach_claim";

export default function ClaimsWrapper({
    allowUserGenerate,
}: ClaimsWrapperProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );

    const [submitType, setSubmitType] = React.useState<SubmitTypes | null>(
        null
    );

    const params = useParams();
    const { noteId, clientId } = params;

    let activeClaimId: string = searchParams.get("active_claim_id") || "";

    const { data, isLoading, error } = useGetClaim(
        { note_id: noteId },
        Boolean(noteId)
    );

    const loggedInUser = useFetchUserDetails();

    const noteDetails = useFetchSessionNote(
        clientId || "",
        noteId || "",
        Boolean(clientId) && Boolean(noteId)
    );

    const invoiceDetails = useFetchInvoices(
        { invoice_id: noteDetails?.data?.data?.invoice_id },
        Boolean(noteDetails?.data?.data?.invoice_id)
    );

    const remitsData = useFetchRemitClaims({
        note_id: noteId,
    });

    const updateClaimsActivity = useUpdateClaimsActivity();
    const splitClaimsToNewClaim = useSplitClaimsToNewClaim();

    const isAddManualRemitMutating = useIsMutating(["add-manual-remit"]);
    const createADraftClaimIsMutating = useIsMutating(["create_draft_claim"]);

    const isSelfPayInvoice = () => {
        const invoiceId = noteDetails.data?.data.invoice_id;

        const invoiceSelfPay =
            invoiceId && invoiceDetails.data?.data?.[0]?.metadata?.self_pay;

        const clientSelfPay = noteDetails.data?.data?.client?.self_pay;

        return Boolean(invoiceSelfPay || clientSelfPay);
    };

    const showGenerateClaimButton = Boolean(invoiceDetails.data);

    const view = searchParams.get("claims_view");

    const claimTabInUrl = searchParams.get("claim_tab") || "content";

    const activeClaimDetails = activeClaimId
        ? data?.data.find((item) => item.claim_id === activeClaimId)
        : data?.data?.[0];

    const activeClaimActivityDetails = getClaimActivityToDisplay(
        activeClaimDetails?.claim_activities || [],
        ClaimActivityType.IN_PROGRESS,
        loggedInUser?.data?.user_id ?? ""
    );
    const activeParentClaimId = activeClaimDetails?.parent_claim_id;
    const activeParentClaimData = useGetParentClaim(
        activeParentClaimId ?? "",
        Boolean(activeParentClaimId)
    );
    const siblingClaimsCount = Math.max(
        (activeParentClaimData.data?.data.child_claims.length ?? 1) - 1,
        0
    );
    const isInMultiClaimGroup =
        Boolean(activeParentClaimId) &&
        (activeParentClaimData.data?.data.child_claims.length ?? 0) > 1;

    const getClaimActivityTextToDisplay =
        activeClaimActivityDetails?.usersNameToDisplay;

    const handleSetTabInUrl = (claimTab: string) => {
        searchParams.set("claim_tab", claimTab);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetClaimInUrl = (claimId: string) => {
        searchParams.set("active_claim_id", claimId);
        setSearchParams(searchParams);
    };

    let claimsContentSwitchOptions: SwitchProps[] = [];

    const queryClient = useQueryClient();
    // Toast for success and error states
    const { toast } = useToast();

    // hook for updating claim status
    const createADraftClaim = useCreateDraftClaim();

    // function to generate a claim
    const handleGenerateClaim = () => {
        const createDraftPayload = {
            note_id: noteId as string,
        };
        createADraftClaim.mutate(createDraftPayload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);
                toast({
                    mode: "success",
                    message: response.message || `Claim created successfully`,
                });
                searchParams.set(
                    "active_claim_id",
                    response.data.claim.claim_id
                );

                setSearchParams(searchParams);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not generate claim at this time",
                });
            },
        });
    };

    const handleUpdateClaimsActivity = (
        activityType: ClaimActivityType,
        claimId: string
    ) => {
        const dataToSend = {
            claim_ids: [claimId],
            activity_type: activityType,
            notes: undefined,
        };
        updateClaimsActivity.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries([
                    "get-claim",
                    { note_id: noteId },
                ]);

                toast({
                    mode: "success",
                    message:
                        res.message || "Claims activity updated successfully!",
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action at this time.",
                });
            },
        });
    };

    const handleDetachClaimFromGroup = () => {
        if (
            !activeParentClaimId ||
            !activeClaimDetails?.claim_id ||
            !isInMultiClaimGroup ||
            splitClaimsToNewClaim.isLoading
        ) {
            return;
        }

        splitClaimsToNewClaim.mutate(
            {
                parentClaimId: activeParentClaimId,
                claimIds: [activeClaimDetails.claim_id],
            },
            {
                onSuccess: (response) => {
                    const newParentClaimId = response.data?.new_parent_claim_id;

                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "Claim detached from the group successfully",
                    });

                    queryClient.invalidateQueries([
                        "get-claim",
                        { note_id: noteId },
                    ]);
                    queryClient.invalidateQueries([
                        "parent-claim",
                        activeParentClaimId,
                    ]);
                    queryClient.invalidateQueries({
                        queryKey: ["grouped-claim-child"],
                    });
                    queryClient.invalidateQueries({
                        predicate: (query) =>
                            query.queryKey[1] === "session-note",
                    });
                    queryClient.invalidateQueries({ queryKey: ["claims"] });
                    queryClient.invalidateQueries({
                        queryKey: ["claims-stats"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["bulk-claim-eligible-notes"],
                    });

                    if (newParentClaimId && clientId) {
                        const navParams = new URLSearchParams({
                            key: "claims",
                            claims_view: "view",
                            viewFrom: "claims",
                            tab: "submission_ready",
                            claims_sub_tab: "claims",
                            active_claim_id: newParentClaimId,
                        });
                        navigate(
                            `/claims/group/${newParentClaimId}/${clientId}?${navParams.toString()}`
                        );
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not detach claim from the group",
                    });
                },
                onSettled: () => {
                    setDialogInView(null);
                },
            }
        );
    };

    const switchOptions = [
        {
            title: "Content",
            key: "content",
            onClick: () => handleSetTabInUrl("content"),
        },
        {
            title: "Claims Activity",
            key: "claims_activity",
            onClick: () => handleSetTabInUrl("claims_activity"),
        },
    ];

    if (data?.data && data.data.length > 1) {
        claimsContentSwitchOptions = data?.data
            .sort(
                (a, b) =>
                    new Date(b.generated_on.replace("Z", "")).getTime() -
                    new Date(a.generated_on.replace("Z", "")).getTime()
            )
            .map((item, i) => ({
                title: (
                    <span className="flex items-center gap-x-1">
                        Claim {formatDate(item.generated_on)} -
                        {item.external_reference_id}
                        {i === 0 ? (
                            <span>
                                <Badge content="Current" type="success" />
                            </span>
                        ) : null}
                        {item.voided_at_payer ? (
                            <span>
                                <Badge
                                    content="Voided at payer"
                                    type="danger"
                                />
                            </span>
                        ) : null}
                    </span>
                ),
                key: item.claim_id,
                onClick: () => handleSetClaimInUrl(item.claim_id),
            }));
    }

    if (!activeClaimId && claimsContentSwitchOptions.length > 1) {
        activeClaimId = claimsContentSwitchOptions[0].key;
    }

    return (
        <>
            <Dialog open={dialogInView === "create-claim-with-insurance"}>
                <DialogContent
                    variant="center"
                    title="Create claim with insurance"
                    handleCloseDialog={() => setDialogInView(null)}
                    submitBtnFormValue="create-claim-with-insurance"
                    isSubmitBtnDisabled={createADraftClaimIsMutating > 0}
                    isCancelBtnDisabled={createADraftClaimIsMutating > 0}
                >
                    <CreateClaimWithInsurance
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "add_remit"}>
                <DialogContent
                    variant="center"
                    title="Add Remit"
                    cancelBtnFormValue="add-remit-form"
                    canCancelButtonSubmit
                    submitBtnFormValue="add-remit-form"
                    handleCloseDialog={() => setDialogInView(null)}
                    cancelText="Save progress"
                    saveText="Continue"
                    onSaveClick={() => {
                        setSubmitType("save-continue");
                    }}
                    isCancelBtnDisabled={isAddManualRemitMutating > 0}
                    isSubmitBtnDisabled={isAddManualRemitMutating > 0}
                    onCancelClick={() => setSubmitType("save")}
                >
                    <ManualRemitForm
                        claimId={activeClaimId || activeClaimDetails?.claim_id}
                        submitType={submitType}
                        onFormSubmit={() => {
                            searchParams.set("claims_sub_tab", "remit");
                            setSearchParams(searchParams);

                            setDialogInView(null);
                        }}
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "detach_claim"}>
                <DialogContent
                    variant="center"
                    title="Detach claim from group?"
                    showFooter
                    cancelText="Cancel"
                    saveText="Detach"
                    onCancelClick={() => setDialogInView(null)}
                    onSaveClick={handleDetachClaimFromGroup}
                    handleCloseDialog={() => setDialogInView(null)}
                    isSubmitBtnDisabled={splitClaimsToNewClaim.isLoading}
                    isCancelBtnDisabled={splitClaimsToNewClaim.isLoading}
                >
                    <p className="text-sm text-textSec">
                        This detaches the claim and resets it to Ready for
                        Submission.
                    </p>
                </DialogContent>
            </Dialog>

            {isLoading && <Loader />}
            {!isLoading &&
                !data?.data &&
                allowUserGenerate &&
                !noteDetails.data?.data.claim_id && (
                    <ListState
                        isError={false}
                        stateHelperText={
                            showGenerateClaimButton
                                ? "Click the button to generate a new claim"
                                : ""
                        }
                        emptyMessage="No claim has been generated for this bill"
                        emptyBtnProps={{
                            showButton: showGenerateClaimButton,
                            buttonText: "Generate Claims",
                            onButtonClick: isSelfPayInvoice()
                                ? () =>
                                      setDialogInView(
                                          "create-claim-with-insurance"
                                      )
                                : handleGenerateClaim,
                            disabled: createADraftClaim.isLoading,
                        }}
                    />
                )}
            {error ? (
                <div>
                    {error?.response?.status === 404 && (
                        <ListState
                            isError={false}
                            stateHelperText="Click the button to generate a new claim"
                            emptyMessage="No claim has been generated for this bill"
                            emptyBtnProps={{
                                showButton: true,
                                buttonText: "Generate Claims",
                                onButtonClick: isSelfPayInvoice()
                                    ? () =>
                                          setDialogInView(
                                              "create-claim-with-insurance"
                                          )
                                    : handleGenerateClaim,
                                disabled: createADraftClaim.isLoading,
                            }}
                        />
                    )}
                </div>
            ) : null}
            {data && data?.data ? (
                <div>
                    {data.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText={
                                showGenerateClaimButton
                                    ? "Click the button to generate a new claim"
                                    : ""
                            }
                            emptyMessage="No claim has been generated for this bill"
                            emptyBtnProps={{
                                showButton: showGenerateClaimButton,
                                buttonText: "Generate Claims",
                                onButtonClick: isSelfPayInvoice()
                                    ? () =>
                                          setDialogInView(
                                              "create-claim-with-insurance"
                                          )
                                    : handleGenerateClaim,
                                disabled: createADraftClaim.isLoading,
                            }}
                        />
                    ) : (
                        <div>
                            {data?.data && data?.data.length > 1 ? (
                                <Card classNames="mb-[--spacing-header] overflow-x-auto">
                                    <Switch
                                        switchOptions={
                                            claimsContentSwitchOptions
                                        }
                                        activeSwitch={activeClaimId}
                                    />
                                </Card>
                            ) : null}
                            {activeParentClaimId ? (
                                <div className="px-3 py-2 mb-3 flex items-center justify-between rounded-md bg-[#acdefa33] border border-[#acdefa]">
                                    <div className="flex items-center gap-x-2">
                                        <Badge
                                            content={
                                                siblingClaimsCount > 0
                                                    ? `Grouped claim (${siblingClaimsCount} sibling${
                                                          siblingClaimsCount > 1
                                                              ? "s"
                                                              : ""
                                                      })`
                                                    : "Grouped claim"
                                            }
                                            type="info"
                                        />
                                        <span className="text-xs text-gray-600">
                                            This claim is part of a group
                                        </span>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        size="auto"
                                        className="h-7 px-3 text-xs"
                                        iconRight={
                                            <ArrowUpRightIcon className="size-3.5" />
                                        }
                                        onClick={() =>
                                            navigate(
                                                `/claims/group/${activeParentClaimId}/${clientId}` +
                                                    `/${noteId}?key=claims&claims_view=view&viewFrom=claims` +
                                                    `&claims_sub_tab=claims&active_claim_id=${activeClaimDetails?.claim_id}`
                                            )
                                        }
                                    >
                                        View group claim
                                    </Button>
                                </div>
                            ) : null}
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        switchOptions={switchOptions}
                                        activeSwitch={claimTabInUrl}
                                    />
                                    <div className="flex flex-col gap-y-1 items-start justify-center">
                                        <span className="text-caption text-textSec font-semibold">
                                            Assigned to
                                        </span>
                                        <span>
                                            {getClaimActivityTextToDisplay}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-x-2">
                                    {isInMultiClaimGroup ? (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                setDialogInView("detach_claim")
                                            }
                                            disabled={
                                                splitClaimsToNewClaim.isLoading
                                            }
                                            iconLeft={
                                                <LogOutIcon className="size-3.5" />
                                            }
                                        >
                                            Detach from group
                                        </Button>
                                    ) : null}
                                    {activeClaimActivityDetails?.isClaimAssignedToMe ? (
                                        <Button
                                            variant="danger"
                                            onClick={() =>
                                                handleUpdateClaimsActivity(
                                                    ClaimActivityType.UNLOCKED,
                                                    activeClaimDetails?.claim_id ??
                                                        ""
                                                )
                                            }
                                            disabled={
                                                updateClaimsActivity.isLoading
                                            }
                                            iconLeft={
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="white"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="size-3.5"
                                                >
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                    <circle
                                                        cx="8.5"
                                                        cy="7"
                                                        r="4"
                                                    />
                                                    <line
                                                        x1="23"
                                                        y1="11"
                                                        x2="17"
                                                        y2="11"
                                                    />
                                                </svg>
                                            }
                                        >
                                            Unassign
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() =>
                                                handleUpdateClaimsActivity(
                                                    ClaimActivityType.IN_PROGRESS,
                                                    activeClaimDetails?.claim_id ??
                                                        ""
                                                )
                                            }
                                            iconLeft={
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="24"
                                                    height="24"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="size-3.5"
                                                >
                                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                    <circle
                                                        cx="8.5"
                                                        cy="7"
                                                        r="4"
                                                    />
                                                    <line
                                                        x1="20"
                                                        y1="8"
                                                        x2="20"
                                                        y2="14"
                                                    />
                                                    <line
                                                        x1="23"
                                                        y1="11"
                                                        x2="17"
                                                        y2="11"
                                                    />
                                                </svg>
                                            }
                                            disabled={
                                                updateClaimsActivity.isLoading
                                            }
                                        >
                                            Assign to me
                                        </Button>
                                    )}

                                    {activeClaimDetails?.claim_status !==
                                        ClaimStatus.READY_FOR_SUBMISSION &&
                                    (!remitsData?.data?.data ||
                                        remitsData?.data?.data?.length ===
                                            0) ? (
                                        <div>
                                            <Button
                                                onClick={() =>
                                                    setDialogInView("add_remit")
                                                }
                                            >
                                                Add remit
                                            </Button>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className="mt-[--spacing-header]">
                                {data?.data && data?.data?.length > 1 ? (
                                    <>
                                        {data.data.map((singleC, i) => {
                                            return activeClaimId ===
                                                singleC.claim_id ? (
                                                <div key={singleC.claim_id}>
                                                    {claimTabInUrl ===
                                                        "content" && (
                                                        <div>
                                                            {view === "edit" ? (
                                                                <EditClaims
                                                                    claimToEditId={
                                                                        singleC.claim_id
                                                                    }
                                                                />
                                                            ) : (
                                                                <SingleClaim
                                                                    claim={
                                                                        singleC
                                                                    }
                                                                    isCurrent={
                                                                        i === 0
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {claimTabInUrl ===
                                                        "claims_activity" && (
                                                        <ClaimSubmissionHistory
                                                            claimId={
                                                                singleC.claim_id
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ) : null;
                                        })}
                                    </>
                                ) : (
                                    <>
                                        {claimTabInUrl === "content" &&
                                        data?.data ? (
                                            <div>
                                                {view === "edit" ? (
                                                    <EditClaims
                                                        claimToEditId={
                                                            data.data[0]
                                                                .claim_id
                                                        }
                                                    />
                                                ) : (
                                                    <SingleClaim
                                                        claim={data.data[0]}
                                                        isCurrent
                                                    />
                                                )}
                                            </div>
                                        ) : null}
                                        {claimTabInUrl === "claims_activity" &&
                                        data?.data ? (
                                            <ClaimSubmissionHistory
                                                claimId={data.data[0].claim_id}
                                            />
                                        ) : null}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : null}
        </>
    );
}
